import { InternalConfig } from './types';

export class FloatingButton {
  private element: HTMLDivElement | null = null;
  private config: InternalConfig;
  private unreadCount: number = 0;
  private onClick: () => void;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private initialX: number = 0;
  private initialY: number = 0;
  private readonly STORAGE_KEY = 'ag-floating-button-position';

  // 预绑定的事件处理函数引用，确保 add/remove 引用一致
  private boundHandleMouseDown: (e: MouseEvent) => void;
  private boundHandleMouseMove: (e: MouseEvent) => void;
  private boundHandleMouseUp: (e: MouseEvent) => void;
  private boundHandleTouchStart: (e: TouchEvent) => void;
  private boundHandleTouchMove: (e: TouchEvent) => void;
  private boundHandleTouchEnd: (e: TouchEvent) => void;

  constructor(config: InternalConfig, onClick: () => void) {
    this.config = config;
    this.onClick = onClick;

    this.boundHandleMouseDown = this.handleMouseDown.bind(this);
    this.boundHandleMouseMove = this.handleMouseMove.bind(this);
    this.boundHandleMouseUp = this.handleMouseUp.bind(this);
    this.boundHandleTouchStart = this.handleTouchStart.bind(this);
    this.boundHandleTouchMove = this.handleTouchMove.bind(this);
    this.boundHandleTouchEnd = this.handleTouchEnd.bind(this);
  }

  public create(): HTMLDivElement {
    if (this.element) {
      return this.element;
    }

    const button = document.createElement('div');
    button.className = 'floating-chat-button';
    button.setAttribute('data-position', this.config.position);
    button.style.width = `${this.config.buttonSize}px`;
    button.style.height = `${this.config.buttonSize}px`;
    
    // 启用拖动
    button.style.cursor = 'pointer';
    button.style.userSelect = 'none';

    // 按钮图标容器
    const iconContainer = document.createElement('div');
    iconContainer.className = 'button-icon';
    iconContainer.style.backgroundColor = this.config.primaryColor;

    // 聊天图标 SVG
    const icon = this.createMainIcon();
    iconContainer.appendChild(icon);

    // 未读消息徽章
    const badge = document.createElement('div');
    badge.className = 'unread-badge';
    badge.style.display = 'none';
    badge.innerHTML = '<span>0</span>';

    button.appendChild(iconContainer);
    button.appendChild(badge);

    // 仅在按钮上绑定 mousedown/touchstart，document 级监听在拖拽开始时动态注册
    button.addEventListener('mousedown', this.boundHandleMouseDown);
    button.addEventListener('touchstart', this.boundHandleTouchStart, { passive: false });

    // 设置初始显示状态
    if (this.config.showButton === false) {
      button.style.display = 'none';
    }

    this.element = button;
    
    // 恢复保存的位置
    this.restorePosition();
    
    return button;
  }

  private savePosition(): void {
    if (!this.element) return;

    try {
      const pos = {
        top: this.element.style.top,
        left: this.element.style.left,
        right: this.element.style.right,
        bottom: this.element.style.bottom,
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(pos));
    } catch (e) {
      console.warn('Failed to save button position:', e);
    }
  }

  private restorePosition(): void {
    if (!this.element) return;

    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        const pos = JSON.parse(saved);
        if (pos.top) this.element.style.top = pos.top;
        if (pos.left) this.element.style.left = pos.left;
        if (pos.right) this.element.style.right = pos.right;
        if (pos.bottom) this.element.style.bottom = pos.bottom;
      }
    } catch (e) {
      console.warn('Failed to restore button position:', e);
    }
  }

  private createMainIcon(): HTMLElement | SVGElement {
    if (this.config.iconUrl) {
      const img = document.createElement('img');
      img.src = this.config.iconUrl;
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'contain';
      return img;
    }
    return this.createChatIcon();
  }

  private createChatIcon(): SVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'white');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z');

    svg.appendChild(path);
    return svg;
  }

  private createCloseIcon(): SVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'white');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z');

    svg.appendChild(path);
    return svg;
  }

  public setIcon(isOpen: boolean): void {
    if (!this.element) return;

    // 如果配置了自定义图标，则不需要切换图标
    if (this.config.iconUrl) return;

    const iconContainer = this.element.querySelector('.button-icon');
    if (!iconContainer) return;

    // 清空现有图标
    iconContainer.innerHTML = '';
    // 添加新图标
    const icon = isOpen ? this.createCloseIcon() : this.createMainIcon();
    iconContainer.appendChild(icon);
  }

  public setUnreadCount(count: number): void {
    this.unreadCount = count;
    this.updateBadge();
  }

  private updateBadge(): void {
    if (!this.element) return;

    const badge = this.element.querySelector('.unread-badge') as HTMLDivElement;
    const span = badge?.querySelector('span');

    if (!badge || !span) return;

    if (this.unreadCount > 0) {
      badge.style.display = 'flex';
      span.textContent = this.unreadCount > 99 ? '99+' : String(this.unreadCount);
    } else {
      badge.style.display = 'none';
    }
  }

  public show(): void {
    if (this.element) {
      this.element.style.display = 'block';
    }
  }

  public hide(): void {
    if (this.element) {
      this.element.style.display = 'none';
    }
  }

  public destroy(): void {
    if (this.element) {
      this.element.removeEventListener('mousedown', this.boundHandleMouseDown);
      this.element.removeEventListener('touchstart', this.boundHandleTouchStart);

      // 安全清理可能残留的 document 级监听（如在拖拽中调用 destroy）
      document.removeEventListener('mousemove', this.boundHandleMouseMove);
      document.removeEventListener('mouseup', this.boundHandleMouseUp);
      document.removeEventListener('touchmove', this.boundHandleTouchMove);
      document.removeEventListener('touchend', this.boundHandleTouchEnd);

      this.element.remove();
      this.element = null;
    }
  }

  private handleMouseDown(e: MouseEvent): void {
    // 只有鼠标左键触发拖拽
    if (e.button !== 0) return;

    this.isDragging = true;
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;

    if (this.element) {
      const rect = this.element.getBoundingClientRect();
      this.initialX = rect.left;
      this.initialY = rect.top;
      this.element.style.transition = 'none';
      // 切换为 left/top 定位，清除 right/bottom 避免冲突
      this.element.style.left = `${rect.left}px`;
      this.element.style.top = `${rect.top}px`;
      this.element.style.right = 'auto';
      this.element.style.bottom = 'auto';
    }

    // 拖拽开始时注册 document 级监听
    document.addEventListener('mousemove', this.boundHandleMouseMove);
    document.addEventListener('mouseup', this.boundHandleMouseUp);

    e.preventDefault();
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.isDragging || !this.element) return;

    const deltaX = e.clientX - this.dragStartX;
    const deltaY = e.clientY - this.dragStartY;

    // 如果移动距离很小，可能是点击而不是拖动（使用欧几里得距离）
    if (Math.sqrt(deltaX * deltaX + deltaY * deltaY) < 5) return;

    const newX = this.initialX + deltaX;
    const newY = this.initialY + deltaY;

    this.updatePosition(newX, newY);
  }

  private handleMouseUp(_e: MouseEvent): void {
    if (!this.isDragging) return;

    // 拖拽结束，移除 document 级监听
    document.removeEventListener('mousemove', this.boundHandleMouseMove);
    document.removeEventListener('mouseup', this.boundHandleMouseUp);

    const deltaX = _e.clientX - this.dragStartX;
    const deltaY = _e.clientY - this.dragStartY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // 如果移动距离小于5px，视为点击
    if (distance < 5) {
      this.onClick();
    } else {
      // 拖动结束后，将固定像素值转换为百分比，以适应缩放
      this.convertToPercentage();
      // 保存位置到本地
      this.savePosition();
    }

    this.isDragging = false;

    if (this.element) {
      // 转换完成后再恢复 transition
      setTimeout(() => {
        if (this.element) {
          this.element.style.transition = '';
        }
      }, 50);
    }
  }

  private handleTouchStart(e: TouchEvent): void {
    if (e.touches.length !== 1) return;

    const touch = e.touches[0];
    this.isDragging = true;
    this.dragStartX = touch.clientX;
    this.dragStartY = touch.clientY;

    if (this.element) {
      const rect = this.element.getBoundingClientRect();
      this.initialX = rect.left;
      this.initialY = rect.top;
      this.element.style.transition = 'none';
      // 切换为 left/top 定位，清除 right/bottom 避免冲突
      this.element.style.left = `${rect.left}px`;
      this.element.style.top = `${rect.top}px`;
      this.element.style.right = 'auto';
      this.element.style.bottom = 'auto';
    }

    // 拖拽开始时注册 document 级触摸监听
    document.addEventListener('touchmove', this.boundHandleTouchMove, { passive: false });
    document.addEventListener('touchend', this.boundHandleTouchEnd);

    e.preventDefault();
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.isDragging || !this.element || e.touches.length !== 1) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - this.dragStartX;
    const deltaY = touch.clientY - this.dragStartY;

    if (Math.sqrt(deltaX * deltaX + deltaY * deltaY) < 5) return;

    const newX = this.initialX + deltaX;
    const newY = this.initialY + deltaY;

    this.updatePosition(newX, newY);
    e.preventDefault();
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.isDragging) return;

    // 拖拽结束，移除 document 级触摸监听
    document.removeEventListener('touchmove', this.boundHandleTouchMove);
    document.removeEventListener('touchend', this.boundHandleTouchEnd);

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - this.dragStartX;
    const deltaY = touch.clientY - this.dragStartY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    if (distance < 5) {
      this.onClick();
    } else {
      // 拖动结束后，将固定像素值转换为百分比，以适应缩放
      this.convertToPercentage();
      // 保存位置到本地
      this.savePosition();
    }

    this.isDragging = false;

    if (this.element) {
      // 转换完成后再恢复 transition
      setTimeout(() => {
        if (this.element) {
          this.element.style.transition = '';
        }
      }, 50);
    }
  }

  private convertToPercentage(): void {
    if (!this.element) return;

    const rect = this.element.getBoundingClientRect();
    const margin = 10;
    const buttonWidth = rect.width;
    const buttonHeight = rect.height;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // 如果按钮尺寸超出视口，固定在边角
    if (buttonHeight > viewportHeight - margin * 2 || buttonWidth > viewportWidth - margin * 2) {
      this.element.style.left = `${margin}px`;
      this.element.style.top = `${margin}px`;
      return;
    }

    // 确保按钮在安全范围内
    let leftDistance = Math.max(margin, Math.min(rect.left, viewportWidth - buttonWidth - margin));
    let topDistance = Math.max(margin, Math.min(rect.top, viewportHeight - buttonHeight - margin));

    // 转换为百分比
    const leftPercent = (leftDistance / viewportWidth) * 100;
    const topPercent = (topDistance / viewportHeight) * 100;

    this.element.style.left = `${Math.max(0, Math.min(100, leftPercent))}%`;
    this.element.style.top = `${Math.max(0, Math.min(100, topPercent))}%`;
    this.element.style.right = 'auto';
    this.element.style.bottom = 'auto';
  }

  private updatePosition(x: number, y: number): void {
    if (!this.element) return;

    const margin = 10;
    const buttonWidth = this.element.offsetWidth;
    const buttonHeight = this.element.offsetHeight;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // 如果按钮太大，无法拖动，固定在左上角
    if (buttonHeight > viewportHeight - margin * 2 || buttonWidth > viewportWidth - margin * 2) {
      this.element.style.left = `${margin}px`;
      this.element.style.top = `${margin}px`;
      this.element.style.right = 'auto';
      this.element.style.bottom = 'auto';
      return;
    }

    // 水平位置限制
    const minX = margin;
    const maxX = viewportWidth - buttonWidth - margin;
    x = Math.max(minX, Math.min(x, maxX));

    // 垂直位置限制
    const minY = margin;
    const maxY = viewportHeight - buttonHeight - margin;
    y = Math.max(minY, Math.min(y, maxY));

    // 使用 left/top 固定像素值定位
    this.element.style.left = `${x}px`;
    this.element.style.top = `${y}px`;
    this.element.style.right = 'auto';
    this.element.style.bottom = 'auto';
  }

  public getElement(): HTMLDivElement | null {
    return this.element;
  }
}
